import { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface CameraCaptureDialogProps {
  isOpen: boolean;
  onCapture: (blob: Blob) => void;
  onCancel: () => void;
  /** Photo is a still shot. Video records the camera with sound. */
  mode?: 'photo' | 'video';
}

export default function CameraCaptureDialog({
  isOpen,
  onCapture,
  onCancel,
  mode = 'photo',
}: CameraCaptureDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewBlobRef = useRef<Blob | null>(null);

  const clearPreview = () => {
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
    previewBlobRef.current = null;
  };

  const showPreview = (blob: Blob) => {
    previewBlobRef.current = blob;
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return URL.createObjectURL(blob);
    });
    setRecording(false);
  };

  const clearTimer = () => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const stopStream = () => {
    clearTimer();
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null;
      recorder.stop();
    }
    recorderRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setRecording(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let stream: MediaStream | null = null;

    const startCamera = async () => {
      setError(null);
      const open = (facingMode: 'user' | 'environment') =>
        navigator.mediaDevices.getUserMedia({
          video: {
            facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: mode === 'video',
        });
      try {
        try {
          stream = await open(mode === 'video' ? 'environment' : 'user');
        } catch {
          stream = await open('user');
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error('Camera error:', err);
        const message =
          err instanceof Error
            ? err.message
            : 'Could not access camera. Please allow camera permission or choose a photo from device.';
        setError(message);
      }
    };

    startCamera();
    return () => {
      clearTimer();
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null;
        recorder.stop();
      }
      recorderRef.current = null;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
      streamRef.current = null;
      setRecording(false);
      setSeconds(0);
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return null;
      });
      previewBlobRef.current = null;
    };
  }, [isOpen, mode]);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || capturing) return;

    setCapturing(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        setCapturing(false);
        return;
      }
      ctx.drawImage(video, 0, 0);
      canvas.toBlob(
        (blob) => {
          setCapturing(false);
          if (blob) showPreview(blob);
        },
        'image/jpeg',
        0.92,
      );
    } catch (err) {
      console.error('Capture failed:', err);
      setCapturing(false);
    }
  };

  const stopVideo = () => {
    clearTimer();
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.onstop = () => {
      const type =
        (recorder.mimeType || 'video/webm').split(';')[0] || 'video/webm';
      const blob = new Blob(chunksRef.current, { type });
      recorderRef.current = null;
      if (blob.size > 0) showPreview(blob);
    };
    recorder.stop();
  };

  const startVideo = () => {
    const stream = streamRef.current;
    if (!stream || recording) return;
    const preferred = [
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
    ].find((item) => MediaRecorder.isTypeSupported(item));
    const recorder = new MediaRecorder(
      stream,
      preferred ? { mimeType: preferred } : undefined,
    );
    chunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorderRef.current = recorder;
    recorder.start(250);
    setRecording(true);
    setSeconds(0);
    timerRef.current = window.setInterval(() => {
      setSeconds((value) => {
        if (value >= 119) {
          stopVideo();
          return value;
        }
        return value + 1;
      });
    }, 1000);
  };

  const retake = () => {
    clearPreview();
    const live = videoRef.current;
    if (live && streamRef.current) {
      live.srcObject = streamRef.current;
      void live.play().catch(() => {});
    }
  };

  const useShot = () => {
    const blob = previewBlobRef.current;
    if (!blob) return;
    clearPreview();
    stopStream();
    onCapture(blob);
  };

  const handleCancel = () => {
    clearPreview();
    stopStream();
    onCancel();
  };

  if (!isOpen) return null;

  const content = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90">
      <div className="absolute inset-0" onClick={handleCancel} aria-hidden />
      <div
        className="relative z-10 bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="camera-dialog-title"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 shrink-0">
          <h3
            id="camera-dialog-title"
            className="text-lg font-semibold text-white"
          >
            {mode === 'video' ? 'Record video' : 'Take picture'}
          </h3>
          <button
            type="button"
            onClick={handleCancel}
            className="p-1.5 rounded-full hover:bg-gray-700 text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative aspect-[4/3] bg-black">
          {error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white">
              <p className="text-sm text-red-300 mb-4">{error}</p>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 rounded-xl bg-gray-600 hover:bg-gray-500"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`h-full w-full object-cover ${previewUrl ? 'hidden' : ''}`}
              />
              {previewUrl && mode === 'photo' && (
                <img
                  src={previewUrl}
                  alt="Photo you just took"
                  className="h-full w-full object-cover"
                />
              )}
              {previewUrl && mode === 'video' && (
                <video
                  src={previewUrl}
                  controls
                  playsInline
                  className="h-full w-full bg-black object-contain"
                />
              )}
              <div className="absolute bottom-4 left-0 right-0 flex flex-col items-center gap-2">
                {previewUrl && mode === 'photo' ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={retake}
                      className="rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-gray-900"
                    >
                      Retake
                    </button>
                    <button
                      type="button"
                      onClick={useShot}
                      className="rounded-full bg-herbal-green px-4 py-2 text-sm font-medium text-white"
                    >
                      Use photo
                    </button>
                  </div>
                ) : null}
                {!previewUrl && mode === 'video' && recording && (
                  <span className="rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white">
                    {Math.floor(seconds / 60)}:
                    {String(seconds % 60).padStart(2, '0')}
                  </span>
                )}
                {!previewUrl &&
                  (mode === 'video' ? (
                    <button
                      type="button"
                      onClick={recording ? stopVideo : startVideo}
                      className={`h-16 w-16 rounded-full border-4 shadow-lg ${
                        recording
                          ? 'border-white bg-red-600'
                          : 'border-gray-300 bg-white'
                      }`}
                      aria-label={recording ? 'Stop video' : 'Start video'}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={handleCapture}
                      disabled={capturing}
                      className="w-16 h-16 rounded-full bg-white border-4 border-gray-300 hover:border-herbal-green disabled:opacity-50 shadow-lg"
                      aria-label="Capture photo"
                    >
                      {capturing ? (
                        <span className="inline-block w-8 h-8 border-2 border-herbal-green border-t-transparent rounded-full animate-spin mx-auto mt-2" />
                      ) : null}
                    </button>
                  ))}
              </div>
            </>
          )}
        </div>
        {previewUrl && mode === 'video' && (
          <div className="flex justify-center gap-2 border-t border-gray-700 px-4 py-3">
            <button
              type="button"
              onClick={retake}
              className="rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-gray-900"
            >
              Retake
            </button>
            <button
              type="button"
              onClick={useShot}
              className="rounded-full bg-herbal-green px-4 py-2 text-sm font-medium text-white"
            >
              Use video
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
